import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { set } from "react-hook-form";

/** Summary → Loan Details Edit: collapse co-applicant accordions so only loan/collateral fields show first. */
export const LOAN_DETAILS_SUMMARY_EDIT_FOCUS_KEY = "__LOAN_DETAILS_LOAN_ONLY__";

export type EntranceExamSection = "core" | "language";

export interface EntranceExamEntry {
  ids: number[];
  fields: any[];
}

export interface EntranceExamEntriesState {
  core: EntranceExamEntry[];
  language: EntranceExamEntry[];
}

interface StepState {
  horizontalSteps: any[];
  displayHorizontalSteps: any[];
  completedHorizontalIndex?: number | null;
  activeHorizontalIndex: number;
  activeVerticalIndex: { [horizontalIndex: number]: number };
  formData: Record<string, any>;
  notInList: string[];
  isMobileVerified: boolean;
  isEmailVerified: boolean;
  loading: boolean;
  isFetchingFormJson: boolean;
  products: { code: string; name: string }[];
  selectedProduct?: string;
  trackingId: string;
  // Set when a partner link's `t` token (decrypted in HorizontalLinearStepper)
  // resolves to a partner-application-fetch response with isPreEligibility
  // true — read by DynamicForm's Loan Details submit handler to skip the
  // live BRE check and show the static success screen instead, and by the
  // eligibility-screen swap to pick which screen renders.
  isPartnerPreEligibilityFlow: boolean;
  // Partner "eligibilitylead" with skipEligibility=true (partner fetch):
  // DynamicForm's Loan Details submit skips the check-eligibility (BRE)
  // call and proceeds as if already qualified.
  // Holds that partner application's trackingId (apply-now?t=...), not a
  // boolean, so the skip can never leak onto any other application.
  partnerSkipEligibilityTrackingId: string;
  collateralId: number | null;
  referenceOneId: number | null;
  referenceTwoId: number | null;
  templateId: number;
  isBypassLogin: boolean;
  isPreLogin: boolean;
  aadhaarApplicationNo: string;
  hasPan: string;
  panNumber: string;
  isPanVerified: boolean;
  adharMandatory: boolean;
  showAadhaar: boolean;
  lastPanNumber: string;
  lastVerifiedPhoneNumber: string;
  lastVerifiedEmail: string;
  isDocumentVerified: boolean;
  isEligibilityCheckSubmitted: boolean;
  isHasCheckedInitialSummary:boolean;
  aadhaarVerifiedDetails: {};
  aadhaarVerifiedDetailsCoApplicant: { [coAppKey: string]: any };
  coApplicantVerification: {
    [index: string]: {
      panVerified?: boolean;
      lastPanNumber?: string;
      emailVerified?: boolean;
      lastVerifiedEmail?: string;
      mobileVerified?: boolean;
      lastVerifiedMobileNumber?: string;
      aadhaarSeedingStatus?: boolean | null;
    };
  };
  currentCoApplicantIdx: string | null;
  extraCourses: any[];
  entranceExamEntries: EntranceExamEntriesState;
  cgpaModes: { [courseNum: string]: boolean };
  extraCoApplicant: any[];
  breStatus: string;
  breData: any[];
  fetchUserApplicationData: any[];
  isEligible: boolean;
  isProvisionalOffer: boolean;
  consentApplicationNo: string;
  partnerStartedJourney: boolean;
  documentUploadId: string;
  isDocumentVerifiedCoApplicant: {};
  aadhaarApplicationNoCoApplicant?: Record<string, string>;
  digiLockerLoaderId: string[];
  documentApplicantType: string;
  aggregatorDocId: string;
  accountAggregatorMobile: string;
  languageExamId: string;
  coreExamId: string;
  languageExamName: string;
  coreExamName: string;
  addedMoreDocumentData: {};
  coApplicantZipCodeData: { [coAppKey: string]: any };
  loanDetailCoApplicantCollapseValue: string;
  checkIsLeadApiInvoked: any;
  leadSequenceId: any;
  assignedSMIdValue: any;
  applicantId: string;
  applicationId: string;
  aadhaarSeedingStatus: boolean | null;
  verificationMessage: {};
  applicationSource: string | null;
  partnerCode: string;
  partnerName: string;
  disableAddMoreCoApplicant: boolean;
  savedBasicCoApplicants: { [coAppKey: string]: boolean };
  savedEmploymentCoApplicants: { [coAppKey: string]: boolean };
  /** Raw PII public key from `getPiiKey` (`p_k`); sanitized when used for RSA. */
  encryptionKey: string;
  customerConsentTime: string;
  /** Summary page: user clicked Edit — show HorizontalLinearStepper form and hide matching ApplicationDetails row. */
  summaryApplicationEditActive: boolean;
  /**
   * Summary: inline form under "Co-Applicant" is shown only when user opened edit from a co-applicant card
   * (not when editing Loan Details under Applicant & Loan — same step indices would match otherwise).
   */
  summaryEditOpenedFromCoApplicantSection: boolean;
  /**
   * Summary: which co-applicant card (by co-applicant number) is currently in
   * inline edit mode. Only one may edit at a time — two mounted FormEdits
   * share the same redux form state and loop each other into
   * "Maximum update depth exceeded".
   */
  summaryEditingCoApplicantKey: string | null;
  /** Monotonic counter to request Formik submit from summary-level Save action. */
  summarySaveRequestId: number;
  /**
   * Which summary editor the latest Save request is for ("application" for the
   * section row Save, "coApplicant<N>" for a co-applicant card Save), so other
   * mounted FormEdits ignore it. null = untargeted (every FormEdit responds).
   */
  summarySaveTarget: string | null;
  /** True while a summary-level Save action is in flight. */
  summarySaveInProgress: boolean;
  /**
   * Set right before a validation guard flips summarySaveInProgress back to false
   * without actually saving, so the summary page's "save just finished" refetch
   * effect can skip the refetch (there's nothing new to fetch, and refetching
   * unmounts the section being edited before the user can see the inline error).
   */
  skipNextSummaryRefetch: boolean;
  isCoAppAdded: boolean;
  /**
   * Increment after co-applicant `save-co-applicant` succeeds so HorizontalLinearStepper
   * can re-run getFormJson even when SUMMARY already has horizontalSteps.
   */
  formJsonRefreshNonce: number;
  coAppAddOption: "provisional" | "co-applicant" | null;
  isCoAppAlreadyExist: boolean;
  googleCampKeys: {};
  isRefColBtnEnable: boolean
}

const initialState: StepState = {
  horizontalSteps: [],
  displayHorizontalSteps: [],
  activeHorizontalIndex: 0,
  activeVerticalIndex: {},
  notInList: [],
  completedHorizontalIndex: null,
  // activeHorizontalIndex: 1, // Start from horizontal step 1
  // activeVerticalIndex: { "0": 0, "1": 5, "2": 0, "3": 0, "4": 0, "5": 0 }, // Start from vertical step 0 of horizontal step 1
  formData: {},
  isMobileVerified: false,
  isEmailVerified: false,
  loading: true,
  isFetchingFormJson: false,
  products: [],
  selectedProduct: "", // or undefined
  trackingId: "",
  isPartnerPreEligibilityFlow: false,
  partnerSkipEligibilityTrackingId: "",
  collateralId: null,
  referenceOneId: null,
  referenceTwoId: null,
  templateId: 0,
  isBypassLogin: false,
  isPreLogin: false,
  aadhaarApplicationNo: "",
  hasPan: "",
  panNumber: "",
  isPanVerified: false,
  adharMandatory: false,
  showAadhaar: false,
  lastPanNumber: "",
  lastVerifiedPhoneNumber: "",
  lastVerifiedEmail: "",
  isDocumentVerified: false,
  isEligibilityCheckSubmitted: false,
  isHasCheckedInitialSummary:false,
  aadhaarVerifiedDetails: {},
  aadhaarVerifiedDetailsCoApplicant: {},
  coApplicantVerification: {},
  currentCoApplicantIdx: null,
  extraCourses: [],
  entranceExamEntries: {
    core: [],
    language: [],
  },
  cgpaModes: {},
  extraCoApplicant: [],
  breStatus: "",
  breData: [],
  fetchUserApplicationData: [],
  isEligible: false,
  isProvisionalOffer: false,
  consentApplicationNo: "",
  partnerStartedJourney: false,
  documentUploadId: "",
  isDocumentVerifiedCoApplicant: {},
  aadhaarApplicationNoCoApplicant: {},
  digiLockerLoaderId: [],
  documentApplicantType: "",
  aggregatorDocId: "",
  accountAggregatorMobile: "",
  languageExamId: "",
  coreExamId: "",
  languageExamName: "",
  coreExamName: "",
  addedMoreDocumentData: {},
  coApplicantZipCodeData: {},
  loanDetailCoApplicantCollapseValue: "CoApplicant1",
  checkIsLeadApiInvoked: {
    ASSIGN_SM: false,
    CREATE_LEAD: false,
    LEAD_TO_APPLICATION: false,
  },
  leadSequenceId: "",
  assignedSMIdValue: "",
  applicantId: "",
  applicationId: "",
  aadhaarSeedingStatus: null,
  verificationMessage: {},
  applicationSource: "",
  partnerCode: "",
  partnerName: "",
  disableAddMoreCoApplicant: true,
  savedBasicCoApplicants: {},
  savedEmploymentCoApplicants: {},
  encryptionKey: "",
  customerConsentTime: "",
  summaryApplicationEditActive: false,
  summaryEditOpenedFromCoApplicantSection: false,
  summaryEditingCoApplicantKey: null,
  summarySaveRequestId: 0,
  summarySaveTarget: null,
  summarySaveInProgress: false,
  skipNextSummaryRefetch: false,
  isCoAppAdded: false,
  formJsonRefreshNonce: 0,
  coAppAddOption: null,
  isCoAppAlreadyExist: false,
  googleCampKeys: {},
  isRefColBtnEnable: false
};

const stepperSlice = createSlice({
  name: "stepper",
  initialState,
  reducers: {
    setHorizontalSteps: (state, action: PayloadAction<any[]>) => {
      state.horizontalSteps = action.payload;
    },
    setDisplayHorizontalSteps: (state, action: PayloadAction<any[]>) => {
      state.displayHorizontalSteps = action.payload;
    },
    setProducts: (
      state,
      action: PayloadAction<{ code: string; name: string }[]>
    ) => {
      state.products = action.payload;
    },
    setSelectedProduct: (state, action: PayloadAction<string>) => {
      state.selectedProduct = action.payload;
    },
    setIsMobileVerified: (state, action: PayloadAction<boolean>) => {
      state.isMobileVerified = action.payload;
    },
    setIsEmailVerified: (state, action: PayloadAction<boolean>) => {
      state.isEmailVerified = action.payload;
    },
    nextVerticalStep: (state) => {
      const hIndex = state.activeHorizontalIndex;
      const verticals = state.horizontalSteps[hIndex]?.verticalSteps || [];
      const currentVertical = state.activeVerticalIndex[hIndex] || 0;

      if (currentVertical < verticals.length - 1) {
        state.activeVerticalIndex[hIndex] = currentVertical + 1;
      } else if (hIndex < state.horizontalSteps.length - 1) {
        state.activeHorizontalIndex++;
        if (
          state.activeVerticalIndex[state.activeHorizontalIndex] === undefined
        ) {
          state.activeVerticalIndex[state.activeHorizontalIndex] = 0;
        }
      }
    },
    prevVerticalStep: (state) => {
      const hIndex = state.activeHorizontalIndex;
      const currentVertical = state.activeVerticalIndex[hIndex] || 0;
      if (currentVertical > 0) {
        state.activeVerticalIndex[hIndex] = currentVertical - 1;
      } else if (hIndex > 0) {
        state.activeHorizontalIndex--;
        const prevHIndex = state.activeHorizontalIndex;
        const prevVerticals =
          state.horizontalSteps[prevHIndex]?.verticalSteps || [];
        state.activeVerticalIndex[prevHIndex] =
          prevVerticals.length > 0 ? prevVerticals.length - 1 : 0;
      }
    },
    updateFormData: (state, action: PayloadAction<Record<string, any>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    clearFormData: (state) => {
      state.formData = {};
    },
    setCompletedHorizontalIndex: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.completedHorizontalIndex = action.payload;
    },
    setActiveHorizontalIndex: (state, action: PayloadAction<number>) => {
      state.activeHorizontalIndex = action.payload;
      if (state.activeVerticalIndex[action.payload] === undefined) {
        state.activeVerticalIndex[action.payload] = 0;
      }
    },
    setActiveVerticalIndex: (state, action: PayloadAction<number>) => {
      state.activeVerticalIndex[state.activeHorizontalIndex] = action.payload;
    },
    clearActiveIndex: (state) => {
      state.activeVerticalIndex = {};
    },
    setFormJson: (
      state,
      action: PayloadAction<{
        horizontalSteps: any[];
        templateId: number;
        section: EntranceExamSection;
        entries: EntranceExamEntry[];
      }>
    ) => {
      state.horizontalSteps = action.payload.horizontalSteps || [];
      state.templateId = action.payload.templateId;

      // Only reset if not already set
      if (Object.keys(state.activeVerticalIndex).length === 0) {
        state.activeHorizontalIndex = 0;
        state.activeVerticalIndex = {};
        if (action.payload.horizontalSteps) {
          action.payload.horizontalSteps.forEach((_: any, idx: number) => {
            state.activeVerticalIndex[idx] = 0;
          });
        }
      }
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsFetchingFormJson: (state, action: PayloadAction<boolean>) => {
      state.isFetchingFormJson = action.payload;
    },
    setTrackingId: (state, action) => {
      state.trackingId = action.payload;
    },
    setPartnerSkipEligibilityTrackingId: (state, action: PayloadAction<string>) => {
      state.partnerSkipEligibilityTrackingId = action.payload;
    },
    setIsPartnerPreEligibilityFlow: (state, action: PayloadAction<boolean>) => {
      state.isPartnerPreEligibilityFlow = action.payload;
    },
    setCollateralId: (state, action) => {
      state.collateralId = action.payload;
    },
    setreferenceOneId: (state, action) => {
      state.referenceOneId = action.payload;
    },
    setreferenceTwoId: (state, action) => {
      state.referenceTwoId = action.payload;
    },
    setApplicationId: (state, action) => {
      state.applicationId = action.payload;
    },
    setApplicantId: (state, action) => {
      state.applicantId = action.payload;
    },
    setIsBypassLogin: (state, action: PayloadAction<boolean>) => {
      state.isBypassLogin = action.payload;
    },
    setIsPreLogin: (state, action: PayloadAction<boolean>) => {
      state.isPreLogin = action.payload;
    },
    setAadhaarApplicationNo: (state, action) => {
      state.aadhaarApplicationNo = action.payload;
    },
    setHasPan: (state, action) => {
      state.hasPan = action.payload;
    },
    setPanNumber: (state, action) => {
      state.panNumber = action.payload;
    },
    setIsPanVerified: (state, action) => {
      state.isPanVerified = action.payload;
    },
    setAadhaarMandatory: (state, action) => {
      state.adharMandatory = action.payload;
    },
    setShowAadhaar: (state, action) => {
      state.showAadhaar = action.payload;
    },
    setLastPanNumber: (state, action) => {
      state.lastPanNumber = action.payload;
    },
    setLastVerifiedPhoneNumber: (state, action) => {
      state.lastVerifiedPhoneNumber = action.payload;
    },
    setLastVerifiedEmail: (state, action) => {
      state.lastVerifiedEmail = action.payload;
    },
    setIsDocumentVerified: (state, action) => {
      state.isDocumentVerified = action.payload;
    },
    setIsEligibilityCheckSubmitted: (state, action) => {
      state.isEligibilityCheckSubmitted = action.payload;
    },
    setHasCheckedInitialSummary: (state, action) => {
      state.isHasCheckedInitialSummary = action.payload;
    },
    setNotInList: (state, action) => {
      state.notInList = [...state.notInList, action.payload];
    },
    removeNotInList: (state, action) => {
      state.notInList = state.notInList.filter(
        (item) => item !== action.payload
      );
    },
    clearNotInList: (state) => {
      state.notInList = [];
    },
    setNotInListFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setAadhaarVerifiedDetails: (state, action) => {
      state.aadhaarVerifiedDetails = action.payload;
    },
    setAadhaarVerifiedDetailsCoApplicant: (state, action) => {
      const { coAppKey, details } = action.payload;
      state.aadhaarVerifiedDetailsCoApplicant = {
        ...state.aadhaarVerifiedDetailsCoApplicant,
        [coAppKey]: details,
      };
    },
    setDobFormValue: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setCoApplicantVerification: (
      state,
      action: PayloadAction<{
        index: string;
        data: {
          panVerified?: boolean;
          lastPanNumber?: string;
          emailVerified?: boolean;
          lastVerifiedEmail?: string;
          mobileVerified?: boolean;
          lastVerifiedMobileNumber?: string;
          aadhaarSeedingStatus?: boolean;
        };
      }>
    ) => {
      const { index, data } = action.payload;
      // Always merge previous state for this co-applicant index
      state.coApplicantVerification[index] = {
        ...(state.coApplicantVerification[index] || {}),
        ...data,
      };
    },
    setCurrentCoApplicantIdx: (state, action: PayloadAction<string | null>) => {
      state.currentCoApplicantIdx = action.payload;
    },
    removeCoApplicantVerification(state, action: PayloadAction<string>) {
      delete state.coApplicantVerification[action.payload];
    },
    setCgpaMode(
      state,
      action: PayloadAction<{ courseNum: string; mode: boolean }>
    ) {
      state.cgpaModes[action.payload.courseNum] = action.payload.mode;
    },
    resetCgpaModes(state) {
      state.cgpaModes = {};
    },
    clearCourseFieldValues(state, action: PayloadAction<string[]>) {
      action.payload.forEach((fieldName) => {
        // If you store values in Redux, clear them here
        if (state.formData) {
          state.formData[fieldName] = "";
        }
      });
    },
    setBreStatus: (state, action) => {
      state.breStatus = action.payload;
    },
    setBreData: (state, action) => {
      state.breData = action.payload;
    },
    setFetchUserApplicationData: (state, action) => {
      state.fetchUserApplicationData = action.payload;
    },
    setIsEligible: (state, action) => {
      state.isEligible = action.payload;
    },
    setIsProvisionalOffer: (state, action) => {
      state.isProvisionalOffer = action.payload;
    },
    setConsentApplicationNo: (state, action) => {
      state.consentApplicationNo = action.payload;
    },
    setDocumentUploadId: (state, action) => {
      state.documentUploadId = action.payload;
    },
    setIsDocumentVerifiedCoApplicant: (state, action) => {
      const { coAppKey, value } = action.payload;
      state.isDocumentVerifiedCoApplicant = {
        ...state.isDocumentVerifiedCoApplicant,
        [coAppKey]: value,
      };
    },
    setAadhaarApplicationNoCoApplicant: (state, action) => {
      const { coAppKey, applicationNo } = action.payload;
      if (!state.aadhaarApplicationNoCoApplicant) {
        state.aadhaarApplicationNoCoApplicant = {};
      }
      state.aadhaarApplicationNoCoApplicant[coAppKey] = applicationNo;
    },
    removeAadhaarVerifiedDetailsCoApplicant: (
      state,
      action: PayloadAction<string>
    ) => {
      // action.payload is the coAppKey, e.g., 'CoApplicant2'
      if (
        state.aadhaarVerifiedDetailsCoApplicant &&
        state.aadhaarVerifiedDetailsCoApplicant[action.payload]
      ) {
        const newObj = { ...state.aadhaarVerifiedDetailsCoApplicant };
        delete newObj[action.payload];
        state.aadhaarVerifiedDetailsCoApplicant = newObj;
      }
    },
    removeIsDocumentVerifiedCoApplicant: (
      state,
      action: PayloadAction<string>
    ) => {
      // action.payload is the coAppKey, e.g., 'CoApplicant2'
      const obj = state.isDocumentVerifiedCoApplicant as Record<string, any>;
      if (obj && obj[action.payload] !== undefined) {
        const newObj = { ...obj };
        delete newObj[action.payload];
        state.isDocumentVerifiedCoApplicant = newObj;
      }
    },
    removeAadhaarApplicationNoCoApplicant: (
      state,
      action: PayloadAction<{ coAppNum: string; coAppKey: string }>
    ) => {
      // Remove both numeric and string keys for this co-applicant
      const obj = state.aadhaarApplicationNoCoApplicant as Record<string, any>;
      if (obj) {
        const newObj = { ...obj };
        delete newObj[action.payload.coAppNum];
        delete newObj[action.payload.coAppKey];
        state.aadhaarApplicationNoCoApplicant = newObj;
      }
    },
    setDigiLockerLoaderId: (state, action) => {
      state.digiLockerLoaderId = [...state.digiLockerLoaderId, action.payload];
    },
    removeDigiLockerLoaderId: (state) => {
      state.digiLockerLoaderId = [];
    },
    setDocumentApplicantType: (state, action) => {
      state.documentApplicantType = action.payload;
    },
    setPartnerStartedJourney: (state, action) => {
      state.partnerStartedJourney = action.payload;
    },
    setAggregatorDocId: (state, action) => {
      state.aggregatorDocId = action.payload;
    },
    setAccountAggregatorMobile: (state, action) => {
      state.accountAggregatorMobile = action.payload;
    },
    setLanguageExamId: (state, action: PayloadAction<string>) => {
      state.languageExamId = action.payload;
    },
    setCoreExamId: (state, action: PayloadAction<string>) => {
      state.coreExamId = action.payload;
    },
    setLanguageExamName: (state, action: PayloadAction<string>) => {
      state.languageExamName = action.payload;
    },
    setCoreExamName: (state, action: PayloadAction<string>) => {
      state.coreExamName = action.payload;
    },
    setAddedMoreDocumentData: (state, action) => {
      state.addedMoreDocumentData = action.payload;
    },
    clearCoApplicantVerification(state) {
      state.coApplicantVerification = {};
    },
    clearIsDocumentVerifiedCoApplicant(state) {
      state.isDocumentVerifiedCoApplicant = {};
    },
    clearAadhaarApplicationNoCoApplicant(state) {
      state.aadhaarApplicationNoCoApplicant = {};
    },
    clearAadhaarVerifiedDetails(state) {
      state.aadhaarVerifiedDetails = {};
    },
    clearAadhaarVerifiedDetailsCoApplicant(state) {
      state.aadhaarVerifiedDetailsCoApplicant = {};
    },
    removeFormDataKeys: (state, action) => {
      const keys: string[] = action.payload;
      keys.forEach((key) => {
        delete state.formData[key];
      });
    },

    setCoApplicantZipCodeData: (state, action) => {

      console.log(action.payload, "action.payload");

      // Deep merge to preserve nested current/permanent data for each co-applicant
      const newData = { ...state.coApplicantZipCodeData };
      
      // Merge each co-applicant's data properly
      Object.keys(action.payload).forEach((key) => {
        if (key === 'coAppKey') {
          // Skip the coAppKey property as it's not part of the data structure
          return;
        }
        
        if (action.payload[key] && typeof action.payload[key] === 'object') {
          // Deep merge: preserve existing current/permanent if new data doesn't have them
          if (newData[key]) {
            newData[key] = {
              current: action.payload[key].current !== undefined 
                ? action.payload[key].current 
                : newData[key].current,
              permanent: action.payload[key].permanent !== undefined 
                ? action.payload[key].permanent 
                : newData[key].permanent,
                company: action.payload[key].company !== undefined 
                ? action.payload[key].company 
                : newData[key].company,
            };
          } else {
            newData[key] = { ...action.payload[key] };
          }
        } else {
          newData[key] = action.payload[key];
        }
      });
      
      state.coApplicantZipCodeData = newData;
    },

    setLoanDetailCoApplicantCollapseValue: (state, action) => {
      state.loanDetailCoApplicantCollapseValue = action.payload;
    },
    clearSetLoanDetailCoApplicantCollapseValue: (state) => {
      state.loanDetailCoApplicantCollapseValue = "CoApplicant1";
    },
    setCheckIsLeadApiInvoked: (state, action) => {
      state.checkIsLeadApiInvoked[action.payload.key] = action.payload.value;
    },
    clearCheckIsLeadApiInvoked: (state) => {
      state.checkIsLeadApiInvoked = {
        ASSIGN_SM: false,
        CREATE_LEAD: false,
        LEAD_TO_APPLICATION: false,
      };
    },
    setLeadSequenceId: (state, action) => {
      state.leadSequenceId = action.payload;
    },
    setAssignedSMIdValue: (state, action) => {
      state.assignedSMIdValue = action.payload;
    },
    setAadhaarSeedingStatus: (state, action) => {
      state.aadhaarSeedingStatus = action.payload;
    },
    setVerificationMessage: (
      state,
      action: PayloadAction<{
        index?: string | number;
        applicantType: "applicant" | "coapplicant" | "";
        field: "pan" | "aadhar" | "";
        status: "success" | "error" | "server-error" | "";
        message: string;
      }>
    ) => {
      state.verificationMessage = action.payload;
    },
    setApplicationSource: (state, action) => {
      state.applicationSource = action.payload;
    },
    setPartnerRouteParams: (
      state,
      action: PayloadAction<{ partnerCode: string; partnerName: string }>
    ) => {
      state.partnerCode = action.payload.partnerCode;
      state.partnerName = action.payload.partnerName;
    },
    clearPartnerRouteParams: (state) => {
      state.partnerCode = "";
      state.partnerName = "";
    },
    setDisableAddMoreCoApplicant: (state, action: PayloadAction<boolean>) => {
      state.disableAddMoreCoApplicant = action.payload;
    },
    setSavedBasicForCoApplicant: (
      state,
      action: PayloadAction<{ coAppKey: string; value: boolean }>
    ) => {
      state.savedBasicCoApplicants = {
        ...state.savedBasicCoApplicants,
        [action.payload.coAppKey]: action.payload.value,
      };
    },
    removeSavedBasicForCoApplicant: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (
        state.savedBasicCoApplicants &&
        state.savedBasicCoApplicants[key] !== undefined
      ) {
        const newObj = { ...state.savedBasicCoApplicants };
        delete newObj[key];
        state.savedBasicCoApplicants = newObj;
      }
    },
    clearSavedBasicCoApplicants: (state) => {
      state.savedBasicCoApplicants = {};
    },
    setSavedEmploymentForCoApplicant: (
      state,
      action: PayloadAction<{ coAppKey: string; value: boolean }>
    ) => {
      state.savedEmploymentCoApplicants = {
        ...state.savedEmploymentCoApplicants,
        [action.payload.coAppKey]: action.payload.value,
      };
    },
    removeSavedEmploymentForCoApplicant: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (
        state.savedEmploymentCoApplicants &&
        state.savedEmploymentCoApplicants[key] !== undefined
      ) {
        const newObj = { ...state.savedEmploymentCoApplicants };
        delete newObj[key];
        state.savedEmploymentCoApplicants = newObj;
      }
    },
    clearSavedEmploymentCoApplicants: (state) => {
      state.savedEmploymentCoApplicants = {};
    },
    setEncryptionKey: (state, action: PayloadAction<string>) => {
      state.encryptionKey = action.payload;
    },
    setCustomerConsentTime: (state, action) => {
      state.customerConsentTime = action.payload;
    },
    setSummaryApplicationEditActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.summaryApplicationEditActive = action.payload;
      if (!action.payload) {
        state.summaryEditOpenedFromCoApplicantSection = false;
        state.summarySaveInProgress = false;
        state.summaryEditingCoApplicantKey = null;
      }
    },
    setSummaryEditingCoApplicantKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.summaryEditingCoApplicantKey = action.payload;
    },
    setSummarySaveInProgress: (state, action: PayloadAction<boolean>) => {
      state.summarySaveInProgress = action.payload;
    },
    setSkipNextSummaryRefetch: (state, action: PayloadAction<boolean>) => {
      state.skipNextSummaryRefetch = action.payload;
    },
    setSummaryEditOpenedFromCoApplicantSection: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.summaryEditOpenedFromCoApplicantSection = action.payload;
    },
    requestSummaryFormSave: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.summarySaveRequestId += 1;
      state.summarySaveTarget = action.payload ?? null;
      state.summarySaveInProgress = true;
    },
    setIsCoAppAdded: (state, action) => {
      state.isCoAppAdded = action.payload;
    },
    setCoAppAddOption: (state, action) => {
      state.coAppAddOption = action.payload;
    },
    setIsCoAppAlreadyExist: (state, action) => {
      state.isCoAppAlreadyExist = action.payload;
    },
    bumpFormJsonRefreshNonce: (state) => {
      state.formJsonRefreshNonce += 1;
    },
    setGoogleCampKeys: (state, action) => {
      state.googleCampKeys = action.payload;
    },
    clearGoogleCampKeys: (state, action) => {
      state.googleCampKeys = {};
    },
    setIsRefColBtnEnable: (state, action) => {
      state.isRefColBtnEnable = action.payload;
    },
  },
});

// Export only actions and reducer from this file
export const {
  setHorizontalSteps,
  setDisplayHorizontalSteps,
  setSelectedProduct,
  nextVerticalStep,
  prevVerticalStep,
  updateFormData,
  setCompletedHorizontalIndex,
  setActiveHorizontalIndex,
  setFormJson,
  setLoading,
  setIsFetchingFormJson,
  setProducts,
  setIsMobileVerified,
  setIsEmailVerified,
  setTrackingId,
  setIsPartnerPreEligibilityFlow,
  setPartnerSkipEligibilityTrackingId,
  setCollateralId,
  setreferenceOneId,
  setreferenceTwoId,
  setApplicationId,
  setApplicantId,
  setIsBypassLogin,
  setIsPreLogin,
  setAadhaarApplicationNo,
  setHasPan,
  setPanNumber,
  setIsPanVerified,
  setAadhaarMandatory,
  setShowAadhaar,
  setLastPanNumber,
  setLastVerifiedPhoneNumber,
  setLastVerifiedEmail,
  setIsDocumentVerified,
  setIsEligibilityCheckSubmitted,
  setHasCheckedInitialSummary,
  setActiveVerticalIndex,
  clearFormData,
  setNotInList,
  removeNotInList,
  setNotInListFormData,
  clearActiveIndex,
  setAadhaarVerifiedDetails,
  setAadhaarVerifiedDetailsCoApplicant,
  removeAadhaarVerifiedDetailsCoApplicant,
  setDobFormValue,
  setCoApplicantVerification,
  setCurrentCoApplicantIdx,
  clearNotInList,
  setCgpaMode,
  resetCgpaModes,
  clearCourseFieldValues,
  removeCoApplicantVerification,
  setBreStatus,
  setBreData,
  setFetchUserApplicationData,
  setIsEligible,
  setIsProvisionalOffer,
  setConsentApplicationNo,
  setDocumentUploadId,
  setIsDocumentVerifiedCoApplicant,
  setAadhaarApplicationNoCoApplicant,
  removeIsDocumentVerifiedCoApplicant,
  removeAadhaarApplicationNoCoApplicant,
  setDigiLockerLoaderId,
  removeDigiLockerLoaderId,
  setDocumentApplicantType,
  setPartnerStartedJourney,
  setAggregatorDocId,
  setAccountAggregatorMobile,
  setLanguageExamId,
  setCoreExamId,
  setLanguageExamName,
  setCoreExamName,
  clearCoApplicantVerification,
  clearIsDocumentVerifiedCoApplicant,
  clearAadhaarApplicationNoCoApplicant,
  clearAadhaarVerifiedDetails,
  clearAadhaarVerifiedDetailsCoApplicant,
  removeFormDataKeys,
  setAddedMoreDocumentData,
  setCoApplicantZipCodeData,
  setLoanDetailCoApplicantCollapseValue,
  setCheckIsLeadApiInvoked,
  setLeadSequenceId,
  clearCheckIsLeadApiInvoked,
  clearSetLoanDetailCoApplicantCollapseValue,
  setAssignedSMIdValue,
  setAadhaarSeedingStatus,
  setVerificationMessage,
  setApplicationSource,
  setPartnerRouteParams,
  clearPartnerRouteParams,
  setDisableAddMoreCoApplicant,
  setSavedBasicForCoApplicant,
  removeSavedBasicForCoApplicant,
  clearSavedBasicCoApplicants,
  setSavedEmploymentForCoApplicant,
  removeSavedEmploymentForCoApplicant,
  clearSavedEmploymentCoApplicants,
  setEncryptionKey,
  setCustomerConsentTime,
  setSummaryApplicationEditActive,
  setSummaryEditOpenedFromCoApplicantSection,
  setSummaryEditingCoApplicantKey,
  setSummarySaveInProgress,
  setSkipNextSummaryRefetch,
  requestSummaryFormSave,
  setIsCoAppAdded,
  bumpFormJsonRefreshNonce,
  setCoAppAddOption,
  setIsCoAppAlreadyExist,
  setGoogleCampKeys,
  clearGoogleCampKeys,
  setIsRefColBtnEnable
} = stepperSlice.actions;
export default stepperSlice.reducer;